import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { fetchCapabilities, refreshExamples, type Capabilities, type Examples } from "@/lib/capabilities";
import { relativeTime } from "@/lib/format";
import { bindPrompt } from "./bindPrompt";
import {
  ALL_PROMPTS,
  CATEGORY_DESCRIPTIONS,
  CATEGORY_LABELS,
  PROMPT_CATEGORIES,
  type LibraryPrompt,
  type PromptCategory,
} from "./library";

/**
 * True when at least one resolved slot came from the live content store
 * (isExample=false) rather than the static sample fallback. Drives the
 * provenance line — mirrors PromptLibraryDialog.hasLiveSlot (kept local
 * to avoid coupling the two dialogs).
 */
function hasLiveSlot(examples: Examples | undefined): boolean {
  if (!examples) return false;
  return (Object.keys(examples) as Array<keyof Examples>)
    .filter((k) => k !== "resolvedAt")
    .some((k) => examples[k]?.isExample === false);
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the picked prompt's question text. The ChatPage uses
   * this to fill the composer's textarea AND immediately fire
   * runAsk() so the picker behaves as a one-click action (matches
   * the typical user expectation of "I picked it, just send").
   */
  onPick: (questionText: string) => void;
}

/**
 * Quick-picker variant of the prompt library — distinct from
 * {@link PromptLibraryDialog} which adds prompts to the user's
 * Saved Questions store. This one is a lightweight chooser:
 *
 *   1. User clicks the 📚 button in the composer.
 *   2. Dialog opens with a category sidebar + searchable list.
 *   3. User picks a prompt → {@link Props#onPick} fires with the
 *      question text → ChatPage fills + sends.
 *
 * <p>Reuses {@link ALL_PROMPTS} so the curation stays in one place;
 * just renders the data with different click semantics. Esc closes,
 * backdrop click closes — standard modal hygiene.
 */
export function PromptPickerDialog({ open, onClose, onPick }: Props) {
  const [activeCategory, setActiveCategory] = useState<PromptCategory>("starter");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Same query key/fetcher ChatPage.tsx and PromptLibraryDialog use — shares
  // the react-query cache so the resolved example objects (and any refresh)
  // are consistent across every prompt surface. Only fetched while open.
  const capabilitiesQuery = useQuery<Capabilities>({
    queryKey: ["capabilities"],
    queryFn: fetchCapabilities,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: open,
  });
  const examples = capabilitiesQuery.data?.examples;

  async function onRefreshSamples() {
    setRefreshing(true);
    try {
      await refreshExamples(); // POST; kicks off a fresh resolve server-side
      const before = examples?.resolvedAt;
      // The resolve runs async server-side — poll the GET a few times rather
      // than looping indefinitely.
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await capabilitiesQuery.refetch();
        if (data?.examples?.resolvedAt !== before) break;
      }
    } catch (err) {
      // Best-effort: a failed refresh just leaves the existing samples in
      // place. Log rather than surfacing an unhandled rejection.
      console.error("Failed to refresh prompt-library sample objects", err);
    } finally {
      setRefreshing(false);
    }
  }

  const provenanceLine =
    examples?.resolvedAt && hasLiveSlot(examples)
      ? `examples from your content store · resolved ${relativeTime(examples.resolvedAt)}`
      : "showing sample objects";

  // Auto-focus the search on open so keyboard users can filter without
  // touching the mouse. The activeCategory + search reset on close so
  // the next open is a clean slate.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  // Esc key closes — standard modal behaviour. Attached to window so
  // focus position inside the dialog doesn't matter.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const trimmedSearch = search.trim().toLowerCase();
  const visible = useMemo<LibraryPrompt[]>(() => {
    const inCategory = trimmedSearch === ""
      ? ALL_PROMPTS.filter((p) => p.category === activeCategory)
      : ALL_PROMPTS;
    if (trimmedSearch === "") return inCategory;
    return inCategory.filter((p) =>
      p.title.toLowerCase().includes(trimmedSearch)
      || p.description.toLowerCase().includes(trimmedSearch)
      || p.question.toLowerCase().includes(trimmedSearch)
      || p.toolsUsed.some((t) => t.toLowerCase().includes(trimmedSearch)),
    );
  }, [activeCategory, trimmedSearch]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-picker-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="flex h-full max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 id="prompt-picker-title" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <BookOpen className="h-4 w-4" aria-hidden="true" />
              Prompt library
              <span className="ml-1 text-xs font-normal text-slate-500">
                {ALL_PROMPTS.length} prompts
              </span>
              <button
                type="button"
                onClick={onRefreshSamples}
                disabled={refreshing}
                aria-label="Refresh samples"
                title="Refresh the sample objects used in these prompts"
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
                Refresh samples
              </button>
            </h2>
            <div className="mt-0.5 text-[11px] text-slate-400">{provenanceLine}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400"
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search by title, description, or tool name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Category sidebar — disabled when searching, since search
              spans all categories. Keeps the scoping intuitive: a
              search term implicitly takes over. */}
          <nav
            className="w-52 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50/60 py-2"
            aria-label="Prompt categories"
          >
            {PROMPT_CATEGORIES.map((c) => {
              const count = ALL_PROMPTS.filter((p) => p.category === c).length;
              const active = trimmedSearch === "" && c === activeCategory;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setActiveCategory(c);
                  }}
                  disabled={trimmedSearch !== ""}
                  className={`block w-full px-3 py-1.5 text-left text-xs transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    active
                      ? "bg-slate-200 font-medium text-slate-900"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <span className="block truncate">{CATEGORY_LABELS[c]}</span>
                  <span className="block text-[10px] text-slate-400">{count} prompts</span>
                </button>
              );
            })}
          </nav>

          <section className="flex flex-1 flex-col overflow-y-auto">
            {trimmedSearch === "" && (
              <div className="border-b border-slate-100 bg-slate-50/40 px-4 py-2 text-[11px] text-slate-600">
                {CATEGORY_DESCRIPTIONS[activeCategory]}
              </div>
            )}

            {visible.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No prompts match {trimmedSearch === "" ? "this category" : `"${search}"`}.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visible.map((p) => {
                  const bound = bindPrompt(p.question, examples);
                  return (
                  <li key={p.id} className="group">
                    <button
                      type="button"
                      onClick={() => {
                        onPick(bound.text);
                        onClose();
                      }}
                      className="block w-full px-4 py-3 text-left transition hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-900">
                            <span>{p.title}</span>
                            {p.starter && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                                Starter
                              </span>
                            )}
                            {p.requiresAdmin && (
                              <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                                Admin
                              </span>
                            )}
                            {p.isWrite && (
                              <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-800">
                                Writes
                              </span>
                            )}
                            {p.requiresCognosSdk && (
                              <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                Live SDK
                              </span>
                            )}
                            {p.requiresLens && (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                Lens
                              </span>
                            )}
                            {bound.usedFallback && (
                              <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                                Sample object
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-slate-600">{p.description}</p>
                          <p className="mt-1 text-xs italic text-slate-500">"{bound.text}"</p>
                        </div>
                        <span className="flex shrink-0 items-center gap-1 rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100">
                          <Send className="h-3 w-3" aria-hidden="true" />
                          Send
                        </span>
                      </div>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
          Click a prompt to send it immediately. Use <kbd className="rounded border border-slate-300 bg-white px-1 text-[10px]">Esc</kbd> to close.
        </footer>
      </div>
    </div>
  );
}
